// /teams/:teamId/members — member roster + invite by email + role changes.
// Admin/owner-only writes per firestore.rules; the UI still renders for
// non-admins (read access), action buttons hidden when not authorized.

import { useEffect, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useTeam, useTeamInvites, useTeamMembers } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import {
  changeMemberRole,
  getCurrentRole,
  inviteMember,
  removeMember,
  revokeInvite,
} from '../../lib/mutations.js'

const ROLES = ['owner', 'admin', 'member']

export function Members() {
  const { teamId } = useParams()
  const [user] = useAuthState(auth)
  const { data: team } = useTeam(teamId)
  const { data: members } = useTeamMembers(teamId)
  const { data: invites } = useTeamInvites(teamId)

  const [currentRole, setCurrentRole] = useState(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch role only when membership count changes
  useEffect(() => {
    if (!user?.uid || !teamId) return
    getCurrentRole({ teamId, uid: user.uid }).then(setCurrentRole)
  }, [teamId, user?.uid, members.length])

  const canManage = currentRole === 'owner' || currentRole === 'admin'

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)
  const [pendingActionId, setPendingActionId] = useState(null)

  async function handleInvite(e) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setSubmitting(true)
    setFormError(null)
    try {
      await inviteMember({
        teamId,
        invitedEmail: inviteEmail,
        role: inviteRole,
        invitedByUid: user.uid,
      })
      setInviteEmail('')
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRoleChange(memberUid, role) {
    setPendingActionId(memberUid)
    try {
      await changeMemberRole({ teamId, memberUid, role })
    } catch (err) {
      setFormError(err.message)
    } finally {
      setPendingActionId(null)
    }
  }

  async function handleRemove(memberUid) {
    setPendingActionId(memberUid)
    try {
      await removeMember({ teamId, memberUid })
    } catch (err) {
      setFormError(err.message)
    } finally {
      setPendingActionId(null)
    }
  }

  async function handleRevoke(inviteId) {
    setPendingActionId(inviteId)
    try {
      await revokeInvite({ teamId, inviteId })
    } catch (err) {
      setFormError(err.message)
    } finally {
      setPendingActionId(null)
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: team?.name ?? teamId, to: `/teams/${teamId}` },
          { label: 'Members' },
        ]}
        title="Members"
        subhead={
          canManage
            ? 'Invite people by email and manage roles.'
            : 'Members in this team. Ask an admin to invite or change roles.'
        }
      />

      <div className="mx-auto max-w-3xl px-6 py-8">
        {canManage && (
          <form
            onSubmit={handleInvite}
            className="mb-8 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-5"
          >
            <h2 className="mb-3 text-sm font-medium text-[var(--color-basalt)]">Invite a member</h2>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block flex-1 min-w-[220px]">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                  Email
                </span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  placeholder="teammate@example.com"
                  className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                  Role
                </span>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit" disabled={submitting} variant="primary" size="md">
                {submitting ? 'Inviting…' : 'Send invite'}
              </Button>
            </div>
            {formError && (
              <p className="mt-3 text-xs text-[var(--color-failed)]" role="alert">
                {formError}
              </p>
            )}
          </form>
        )}

        <section className="mb-8">
          <h2 className="mb-3 text-sm font-medium text-[var(--color-basalt)]">Members</h2>
          {members.length === 0 ? (
            <p className="text-sm text-[var(--color-schist)]">No members yet.</p>
          ) : (
            <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
              {members.map((m) => {
                const isSelf = m.uid === user?.uid
                return (
                  <li
                    key={m.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                  >
                    <div>
                      <div className="font-medium text-[var(--color-basalt)]">
                        {m.email ?? m.displayName ?? m.uid}
                        {isSelf && (
                          <span className="ml-2 text-xs text-[var(--color-schist)]">(you)</span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-[var(--color-schist)]">{m.uid}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canManage && !isSelf ? (
                        <>
                          <select
                            value={m.role}
                            onChange={(e) => handleRoleChange(m.uid, e.target.value)}
                            disabled={pendingActionId === m.uid}
                            className="rounded-md border border-[var(--color-sandstone)] bg-white px-2 py-1 text-xs"
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={pendingActionId === m.uid}
                            onClick={() => handleRemove(m.uid)}
                          >
                            Remove
                          </Button>
                        </>
                      ) : (
                        <span className="rounded-sm bg-[var(--color-sandstone)] px-2 py-0.5 text-xs uppercase tracking-wider text-[var(--color-slate)]">
                          {m.role}
                        </span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {invites.length > 0 && (
          <section>
            <h2 className="mb-3 text-sm font-medium text-[var(--color-basalt)]">Pending invites</h2>
            <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
              {invites.map((inv) => (
                <li
                  key={inv.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                >
                  <div>
                    <div className="font-medium text-[var(--color-basalt)]">{inv.invitedEmail}</div>
                    <div className="text-xs text-[var(--color-schist)]">role: {inv.role}</div>
                  </div>
                  {canManage && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={pendingActionId === inv.id}
                      onClick={() => handleRevoke(inv.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  )
}
